/**
 * Otto Agent → email-campaign draft.
 *
 * Otto pre-fills a NEW campaign in create mode (subject + body + layout +
 * recipients); the human reviews and clicks Send. It never sends. Otto uses the
 * two FRAGMENT layouts (`simple_text` / `ceo_letter`) so `content` is a plain
 * HTML body fragment it can author reliably — crm-api's mailer wraps it in the
 * logo header + greeting + footer/unsubscribe chrome. The section-built layouts
 * need the in-browser builder's `content_data`, which Otto can't synthesize.
 */
import type { TargetSelection } from '@crm-src/components/email-target-selector';

/** The fragment layouts Otto may drive. */
export const OTTO_CAMPAIGN_LAYOUTS = ['simple_text', 'ceo_letter'] as const;
export type OttoCampaignLayout = (typeof OTTO_CAMPAIGN_LAYOUTS)[number];

export interface OttoCampaignDraft {
  subject: string;
  layout: OttoCampaignLayout;
  /** HTML body fragment (crm-api adds the surrounding chrome). */
  content: string;
  target_selection: TargetSelection;
  from_address?: string;
  from_name?: string;
}

/**
 * Validate an untrusted Otto campaign draft. Throws when it's missing what a
 * reviewable draft needs: a subject, a fragment layout, body content, and at
 * least one recipient / group / industry segment. Returns the narrowed draft.
 */
export function validateOttoCampaignDraft(value: unknown): OttoCampaignDraft {
  const d = value as Partial<OttoCampaignDraft> | null;
  if (!d || typeof d !== 'object') throw new Error('Otto campaign draft is not an object');
  if (!(d.subject ?? '').trim()) throw new Error('Otto campaign draft is missing a subject');
  if (!OTTO_CAMPAIGN_LAYOUTS.includes(d.layout as OttoCampaignLayout)) {
    throw new Error('Otto campaign draft must use a simple_text or ceo_letter layout');
  }
  if (!(d.content ?? '').trim()) throw new Error('Otto campaign draft is missing body content');
  const ts = d.target_selection;
  const hasAudience =
    !!ts &&
    (((ts.recipients?.length ?? 0) > 0) ||
      ((ts.groups?.length ?? 0) > 0) ||
      ((ts.filters?.industries?.length ?? 0) > 0));
  if (!hasAudience) throw new Error('Otto campaign draft has no recipients');
  // Normalize the audience shape: the campaign form reads
  // `target_selection.recipients.length` on render, so `recipients` MUST be an
  // array (Otto often sends only groups/industries → no recipients key → crash).
  const normalized: TargetSelection = {
    recipients: Array.isArray(ts!.recipients) ? ts!.recipients : [],
    ...(Array.isArray(ts!.groups) && ts!.groups.length ? { groups: ts!.groups } : {}),
    ...(ts!.filters ? { filters: ts!.filters } : {}),
  };
  return { ...(d as OttoCampaignDraft), target_selection: normalized };
}
